import "./boxFilters.css";
import {
  boxesSubCategories,
  getBoxCategories,
} from "../../api/modules/b2droppie.class";
import { useSelector } from "react-redux";
import { useState, useRef, useEffect } from "react";
import { Search, ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";

const BoxFilters = ({
  category,
  hideThis,
  setCategory,
  sortFilter,
  searchQuery,
  boxCategories,
  subCategories,
  setSearchQuery,
  onSearchChange,
  setBoxCategories,
  setSubCategories,
  selectedSubCategories,
  setSortFilter = () => {},
  setSelectedSubCategories,
}) => {
  const tabsRef = useRef(null);
  const dropdownRef = useRef(null);
  const sortDropdownRef = useRef(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isSortFilterOpen, setIsSortFilterOpen] = useState(false);
  const isDarkMode = useSelector((state) => state?.theme?.darkMode);

  const SORT_FILTERS = [
    { label: "Risk: Low to High", filter_by: "risk_level", order_by: "asc" },
    { label: "Risk: High to Low", filter_by: "risk_level", order_by: "desc" },
    { label: "Newest to Oldest", filter_by: "created_at", order_by: "desc" },
    { label: "Oldest to Newest", filter_by: "created_at", order_by: "asc" },
    { label: "Price: High to Low", filter_by: "box_price", order_by: "desc" },
    { label: "Price: Low to High", filter_by: "box_price", order_by: "asc" },
    { label: "Name: A-Z", filter_by: "name", order_by: "asc" },
    { label: "Name: Z-A", filter_by: "name", order_by: "desc" },
  ];

  useEffect(() => {
    fetchBoxeCategories();
    getBoxesSubCategories();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
      if (
        sortDropdownRef.current &&
        !sortDropdownRef.current.contains(event.target)
      ) {
        setIsSortFilterOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const fetchBoxeCategories = async () => {
    try {
      const response = await getBoxCategories();
      const categoryData = response?.data?.data || [];

      const filtered = categoryData.filter(
        (cat) => cat.name?.toLowerCase() !== "all",
      );

      const categoriesWithAllFirst = [{ id: "all", name: "All" }, ...filtered];

      setBoxCategories(categoriesWithAllFirst);
    } catch (error) {
      console.error("Error fetching box categories:", error);
    }
  };

  const getBoxesSubCategories = async () => {
    try {
      const response = await boxesSubCategories();
      setSubCategories(response?.data?.data || []);
    } catch (error) {
      console.error(error);
    }
  };

  const scroll = (direction) => {
    if (tabsRef.current) {
      const container = tabsRef.current;
      const scrollAmount = 300;

      if (direction === "left") {
        const currentScroll = container.scrollLeft;
        if (currentScroll <= 0) {
          container.scrollTo({
            left: container.scrollWidth - container.clientWidth,
            behavior: "smooth",
          });
        } else {
          container.scrollTo({
            left: currentScroll - scrollAmount,
            behavior: "smooth",
          });
        }
      } else {
        const currentScroll = container.scrollLeft;
        const maxScroll = container.scrollWidth - container.clientWidth;
        if (currentScroll >= maxScroll - 5) {
          container.scrollTo({
            left: 0,
            behavior: "smooth",
          });
        } else {
          container.scrollTo({
            left: currentScroll + scrollAmount,
            behavior: "smooth",
          });
        }
      }
    }
  };

  const toggleSubCategory = (id) => {
    setSelectedSubCategories((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  };

  return (
    <div
      className={`bf-main-wrapper p-0 ${isDarkMode ? "bg-smoky-black dark" : "bg-white light"}`}
    >
      <div className="bf-container">
        {/* Search Box */}
        <div className="bf-search-box">
          <Search className="bf-search-icon" size={18} />
          <input
            type="text"
            value={searchQuery}
            placeholder="Search"
            className={`bf-search-input ${isDarkMode ? "bg-jet-black" : "bg-light-gray"}`}
            onChange={(e) => {
              const value = e.target.value;
              setSearchQuery(value);
              if (typeof onSearchChange === "function") onSearchChange(value);
            }}
          />
        </div>

        {/* Category Dropdown */}
        <div className="bf-dropdown-wrap" ref={dropdownRef}>
          <span
            className={`bf-dropdown-label ${isDarkMode ? "text-cool-gray" : "text-dark-charcoal"}`}
          >
            Sort By:
          </span>
          <div className="bf-dropdown">
            <button
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className={`bf-dropdown-btn ${isDarkMode ? "bg-jet-black" : "bg-light-gray"}`}
            >
              <span
                className={`bf-dropdown-text ${isDarkMode ? "text-white" : "text-black"}`}
              >
                {category}
              </span>
              <ChevronDown
                size={16}
                className={`bf-dropdown-chevron ${isDarkMode ? "text-white" : "text-black"} ${
                  isDropdownOpen ? "bf-chevron-open" : ""
                }`}
              />
            </button>

            {isDropdownOpen && (
              <div
                className={`bf-dropdown-menu ${isDarkMode ? "bf-dropdown-menu-dark" : "bf-dropdown-menu-light"}`}
              >
                {boxCategories.map((item, index) => (
                  <button
                    key={index}
                    onClick={() => {
                      setCategory(item.name);
                      setIsDropdownOpen(false);
                    }}
                    className={`bf-dropdown-item ${isDarkMode ? "bf-dropdown-item-dark" : "bf-dropdown-item-light"}`}
                  >
                    {item.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Scrollable Tabs */}
        <div
          className="bf-tabs-wrapper"
          style={{ maxWidth: hideThis ? "100%" : "" }}
        >
          <button
            className={`bf-tab-arrow bf-tab-arrow-left
              ${isDarkMode ? "bg-jet-black text-white" : "bg-light-gray text-charcoal-gray"}`}
            onClick={() => scroll("left")}
            aria-label="Scroll left"
          >
            <ChevronLeft size={18} />
          </button>

          <div className="bf-tabs-scroll" ref={tabsRef}>
            <div className="bf-tabs-inner">
              {subCategories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => toggleSubCategory(cat.id)}
                  className={`bf-tab-btn ${isDarkMode ? "bg-jet-black  text-light-gray" : "bg-light-gray text-charcoal-gray"} ${
                    selectedSubCategories.includes(cat.id)
                      ? isDarkMode
                        ? "bf-tab-btn-active-dark"
                        : "bf-tab-btn-active-light"
                      : "bf-tab-btn"
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>
          </div>

          <button
            className={`bf-tab-arrow bf-tab-arrow-right
              ${isDarkMode ? "bg-jet-black text-white" : "bg-light-gray text-charcoal-gray"}`}
            onClick={() => scroll("right")}
            aria-label="Scroll right"
          >
            <ChevronRight size={18} />
          </button>
        </div>

        {/* Sort Filter Dropdown */}
        {!hideThis && (
          <div className="bf-dropdown-wrap" ref={sortDropdownRef}>
            <span className="bf-dropdown-label">Order By:</span>
            <div className="bf-dropdown">
              <button
                className="bf-dropdown-btn"
                onClick={() => setIsSortFilterOpen(!isSortFilterOpen)}
              >
                <span className="bf-dropdown-text">
                  {
                    SORT_FILTERS.find(
                      (f) =>
                        f?.filter_by === sortFilter?.filter_by &&
                        f?.order_by === sortFilter?.order_by,
                    )?.label
                  }
                </span>
                <ChevronDown
                  size={16}
                  className={`bf-dropdown-chevron ${
                    isSortFilterOpen ? "bf-chevron-open" : ""
                  }`}
                />
              </button>

              {isSortFilterOpen && (
                <div
                  className={`bf-dropdown-menu ${isDarkMode ? "bf-dropdown-menu-dark" : "bf-dropdown-menu-light"}`}
                >
                  {SORT_FILTERS.map((item, index) => (
                    <button
                      key={index}
                      className="bf-dropdown-item"
                      onClick={() => {
                        setSortFilter({
                          filter_by: item.filter_by,
                          order_by: item.order_by,
                        });
                        setIsSortFilterOpen(false);
                      }}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BoxFilters;
